import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { ROLE_LABELS, isInternal } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox } from "@/components/TableTools";
import { numLang } from "@/lib/num";
import { dateLocale, actLabel } from "@/lib/txnI18n";

export default function AuditTrail() {
  const { selectedClientId } = useAuth();
  const { t } = useI18n();
  const [logs, setLogs] = useState([]);

  const load = useCallback(async () => {
    if (!selectedClientId) return;
    const { data } = await api.get(`/audit-logs?client_id=${selectedClientId}`);
    setLogs(data);
  }, [selectedClientId]);
  useEffect(() => { load(); }, [load]);
  const tools = useTableTools(logs, { date: (l) => l.timestamp, text: (l) => `${l.entity_type} ${l.action} ${l.user_name} ${ROLE_LABELS[l.role] || ""} ${JSON.stringify(l.changes || {})}` });
  const cols = [["Waktu", (l) => new Date(l.timestamp).toLocaleString(dateLocale(numLang()))], ["Entitas", "entity_type"], ["Aksi", (l) => actLabel(l.action, numLang())], ["Oleh", "user_name"],
    ["Role", (l) => ROLE_LABELS[l.role] || l.role], ["Detail", (l) => Object.entries(l.changes || {}).map(([k, v]) => `${k}: ${v !== null && typeof v === "object" ? JSON.stringify(v) : v}`).join("; ")]];

  return (
    <div className="space-y-4" data-testid="audit-page">
      <div className="page-sticky">
        <h1 className="font-head text-2xl font-bold tracking-tight">{t("audit.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">Riwayat penuh perubahan data & status.</p>
      </div>
      <TableToolbar tools={tools} id="audit" cols={cols} filename="audit_trail" />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead className="w-8"><SelectAllBox tools={tools} id="audit" /></TableHead>
            <TableHead>Waktu</TableHead><TableHead>Entitas</TableHead><TableHead>Aksi</TableHead>
            <TableHead>Oleh</TableHead><TableHead>Detail</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tools.view.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Belum ada aktivitas</TableCell></TableRow>}
            {tools.view.map((l) => (
              <TableRow key={l.id} data-testid="audit-row">
                <TableCell><SelectBox tools={tools} row={l} id="audit" /></TableCell>
                <TableCell className="num text-xs whitespace-nowrap">{new Date(l.timestamp).toLocaleString("id-ID")}</TableCell>
                <TableCell className="text-xs capitalize">{l.entity_type}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{l.action}</Badge></TableCell>
                <TableCell className="text-xs">{l.user_name} <span className="text-muted-foreground">· {ROLE_LABELS[l.role]}</span></TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">{JSON.stringify(l.changes)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
